import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFunctions } from 'firebase/functions';
import { getAnalytics, logEvent, isSupported } from 'firebase/analytics';
import { getStorage } from 'firebase/storage';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
let app;
let db;
let auth;
let functions;
let analytics;
let storage;

// Event queue for analytics events that occur before initialization
const analyticsEventQueue = [];
let analyticsReady = false;

// Queued log event function - queues events if analytics not ready, otherwise logs directly
const queuedLogEvent = (analyticsInstance, eventName, eventParams) => {
  if (analyticsReady && analyticsInstance) {
    // Analytics is ready, log directly
    logEvent(analyticsInstance, eventName, eventParams);
  } else if (!analyticsReady) {
    // Analytics not ready yet, queue the event
    analyticsEventQueue.push({ eventName, eventParams });
  }
  // If analyticsReady but no analyticsInstance, silently drop (analytics not supported)
};

// Process queued events once analytics is ready
const processEventQueue = () => {
  if (analytics && analyticsEventQueue.length > 0) {
    analyticsEventQueue.forEach(({ eventName, eventParams }) => {
      try {
        logEvent(analytics, eventName, eventParams);
      } catch (err) {
        console.warn('Analytics error processing queued event:', err.message);
      }
    });
    analyticsEventQueue.length = 0;
  }
  analyticsReady = true;
};

try {
  app = initializeApp(firebaseConfig);

  // Get database ID from environment (defaults to '(default)')
  const databaseId = process.env.REACT_APP_FIREBASE_DATABASE_ID || '(default)';

  // Initialize Firestore with specific database
  db = getFirestore(app, databaseId);

  // Initialize Firebase Auth
  auth = getAuth(app);

  // Set explicit persistence to LOCAL (persists across browser sessions)
  setPersistence(auth, browserLocalPersistence)
    .catch((error) => {
      console.error('Error setting Firebase Auth persistence:', error);
    });

  // Initialize Firebase Functions
  functions = getFunctions(app);

  // Initialize Firebase Storage
  storage = getStorage(app);

  // Initialize Firebase Analytics (only in browser)
  if (typeof window !== 'undefined') {
    isSupported().then(supported => {
      if (supported) {
        try {
          analytics = getAnalytics(app);
          processEventQueue();
        } catch (err) {
          console.warn('Analytics initialization failed (may be blocked by ad blocker):', err.message);
          analyticsReady = true;
        }
      } else {
        analyticsReady = true;
      }
    }).catch(err => {
      console.warn('Analytics not supported:', err.message);
      analyticsReady = true;
    });
  }

} catch (error) {
  console.error('Firebase initialization error:', error);
}

export { db, auth, functions, analytics, storage, logEvent, queuedLogEvent };
export default app;
